import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { App, Button, Card, Form, Input, Modal, Skeleton, Space, Table, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ContainerFull } from "../../components"
import { ShowMessagePage } from "../../config"
import {
    createUserGroup,
    getUserGroups,
    UserGroup
} from "../../endpoints"

const { Title } = Typography
const { TextArea } = Input

export function UserGroups(): JSX.Element {
    const [selectedRow, setSelectedRow] = useState<number>(-1)
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [form] = Form.useForm()
    
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { modal } = App.useApp()
    
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getUserGroups"],
        queryFn: getUserGroups,
    })

    const createMutation = useMutation({
        mutationFn: createUserGroup,
        onSuccess: (data) => {
            if (data.code === 1000) {
                modal.success({
                    title: "Success",
                    content: data.message,
                })
                setIsModalVisible(false)
                form.resetFields()
                queryClient.invalidateQueries({ queryKey: ["getUserGroups"] })
            } else {
                modal.error({
                    title: "Error",
                    content: data.message,
                })
            }
        },
        onError: (error) => {
            modal.error({
                title: "Error",
                content: "Failed to create user group",
            })
        },
    })



 
    const handleCreate = () => {
        form.resetFields()
        setIsModalVisible(true)
    }

    const handleEdit = (group: UserGroup) => {
        navigate(`/admin/usergroup/${group.id}`)
    }

    // const handleDelete = (id: number) => {
    //     deleteMutation.mutate({ id })
    // }

    const handleModalOk = () => {
        form.validateFields().then((values) => {
            // Trim all string values before sending to backend
            const trimmedValues = Object.keys(values).reduce((acc, key) => {
                const value = values[key]
                acc[key] = typeof value === 'string' ? value.trim() : value
                return acc
            }, {} as any)

            createMutation.mutate(trimmedValues)
        })
    }

    const handleModalCancel = () => {
        setIsModalVisible(false)
        form.resetFields()
    }

    if (isPending) {
        return (
            <ContainerFull>
                <Skeleton active />
            </ContainerFull>
        )
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    const columns: ColumnsType<UserGroup> = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: "Description", 
            dataIndex: "description",
            key: "description",
            ellipsis: true,
        },
        {
            title: "API Key",
            dataIndex: "api_key",
            key: "api_key",
            render: (text) => text ? "••••••••" : "-",
        },
        {
            title: "AWS Profile",
            dataIndex: "aws_profile", 
            key: "aws_profile",
            render: (text) => text || "-",
        },
        {
            title: "AWS Bucket",
            dataIndex: "aws_bucket_name", 
            key: "aws_bucket_name",
            render: (text) => text || "-",
        },
        {
            title: "Prefix",
            dataIndex: "fileprefix", 
            key: "fileprefix",
            render: (text) => text || "-",
        },
        {
            title: "Billing Contact",
            dataIndex: "billingContactName", 
            key: "billingContactName",
            render: (text) => text || "-",
        },
        {
            title: "Billing Email",
            dataIndex: "billingEmail", 
            key: "billingEmail",
            render: (text) => text || "-",
        },
        {
            title: "Users Receiving Order Notifications",
            dataIndex: "users_receiving_notifications",
            key: "users_receiving_notifications",
            
            render: (users: Array<{firstName: string, lastName: string, id: number}>) => {
                if (!users || users.length === 0) return "-"
                return users.map(u => `${u.firstName} ${u.lastName}`).join(", ")
            },
        },
        {
            title: "Users View All Orders",
            dataIndex: "users_viewing_orders",
            key: "users_viewing_orders",
            
            render: (users: Array<{firstName: string, lastName: string, id: number}>) => {
                if (!users || users.length === 0) return "-"
                return users.map(u => `${u.firstName} ${u.lastName}`).join(", ")
            },
        },
        {
            title: "Created",
            dataIndex: "ts",
            key: "ts",
            width: 120,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                // <Space>
                
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    /*{ <Popconfirm
                        title="Delete User Group"
                        description="Are you sure you want to delete this user group?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm> }*/
                // </Space>
            ),
        },
    ]

    return (
        <ContainerFull>
            <Card>
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Title level={3}>User Groups Management</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Create Group
                    </Button>
                </div>
                
                <Table
                    columns={columns}
                    dataSource={data?.groups || []}
                    rowKey="id"
                    pagination={{
                        total: data?.groups?.length || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} groups`,
                    }}
                    onRow={(record) => ({
                        onClick: () => {
                            setSelectedRow(record.id)
                        },
                    })}
                    rowClassName={(record) =>
                        selectedRow === record.id ? "ant-table-row-selected" : ""
                    }
                />
            </Card>

            <Modal
                title="Create User Group"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                confirmLoading={createMutation.isPending}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="userGroupForm"
                >
                    <Form.Item
                        label="Group Name"
                        name="name"
                        rules={[
                            { required: true, message: "Please enter a group name" },
                            { max: 100, message: "Group name cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter group name" />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                        rules={[
                            { max: 500, message: "Description cannot exceed 500 characters" },
                        ]}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="Enter group description (optional)" 
                        />
                    </Form.Item>

                    <Form.Item
                        label="API Key"
                        name="api_key"
                        rules={[
                            { max: 200, message: "API key cannot exceed 200 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Enter API key (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="AWS Profile"
                        name="aws_profile"
                        rules={[
                            { max: 100, message: "AWS profile cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter AWS profile (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="AWS Bucket Name"
                        name="aws_bucket_name"
                        rules={[
                            { max: 300, message: "AWS bucket name cannot exceed 300 characters" },
                        ]}
                    >
                        <Input placeholder="Enter AWS S3 bucket name" />
                    </Form.Item>

                    <div style={{ marginTop: 24, marginBottom: 16 }}>
                        <Typography.Title level={5}>Billing Information</Typography.Title>
                    </div>

                    <Form.Item
                        label="Billing Institution"
                        name="billingInstitution"
                        rules={[
                            { max: 200, message: "Institution name cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter institution/company name" />
                    </Form.Item>

                    <Form.Item
                        label="Billing Contact Name"
                        name="billingContactName"
                        rules={[
                            { max: 100, message: "Contact name cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter billing contact name" />
                    </Form.Item>

                    <Form.Item
                        label="Billing Email"
                        name="billingEmail"
                        rules={[
                            { 
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve()
                                    // Trim whitespace and validate
                                    const trimmedValue = value.trim()
                                    if (!trimmedValue) return Promise.resolve()
                                    
                                    // More permissive email validation that handles most valid email formats
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                                    if (emailRegex.test(trimmedValue)) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(new Error('Please enter a valid email address'))
                                }
                            },
                            { max: 100, message: "Email cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter billing email address" />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 1"
                        name="billingAddressLine1"
                        rules={[
                            { max: 200, message: "Address line 1 cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter street address" />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 2"
                        name="billingAddressLine2"
                        rules={[
                            { max: 200, message: "Address line 2 cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter apartment, suite, etc. (optional)" />
                    </Form.Item>

                    <Space.Compact style={{ display: 'flex', gap: 8 }}>
                        <Form.Item
                            label="City"
                            name="billingCity"
                            style={{ flex: 1 }}
                            rules={[
                                { max: 100, message: "City cannot exceed 100 characters" },
                            ]}
                        >
                            <Input placeholder="Enter city" />
                        </Form.Item>

                        <Form.Item
                            label="State"
                            name="billingState"
                            style={{ width: 160 }}
                            rules={[
                                { max: 30, message: "State cannot exceed 30 characters" },
                            ]}
                        >
                            <Input placeholder="State" />
                        </Form.Item>

                        <Form.Item
                            label="ZIP Code"
                            name="billingZipcode"
                            style={{ width: 120 }}
                            rules={[
                                { max: 20, message: "ZIP code cannot exceed 20 characters" },
                            ]}
                        >
                            <Input placeholder="ZIP" />
                        </Form.Item>
                    </Space.Compact>

                    <Form.Item
                        label="Country"
                        name="billingCountry"
                        rules={[
                            { max: 50, message: "Country cannot exceed 50 characters" },
                        ]}
                        initialValue="USA"
                    >
                        <Input placeholder="Enter country" />
                    </Form.Item>
                </Form>
            </Modal>
        </ContainerFull>
    )
}
